import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormGroupType } from '@app/core/utils/formgroup-type';
import { CreateMeteringPointCommand, UpdateMeteringPointCommand } from '@app/api/dmc';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SearchComponent } from '@shared/components/input/search/search.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AutocompleteService } from '@app/core/services/autocomplete.service';
import { ToastService } from '@app/core/services/toast.service';
import { NgClass } from '@angular/common';
import { SearchConfig } from '@shared/components/input/search/search-config.interface';
import { catchError, Observable, of } from 'rxjs';
import { SearchResult, SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { map } from 'rxjs/operators';
import { mapDarResults } from '@app/core/utils/map-dar-results';
import { patchAddress } from '@app/core/utils/patch-address';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { AddressDto, ConnectionPointDto } from '@app/api/nts';
import { AddressType } from '@app/core/types/address-type';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { SelectDirective } from '@shared/components/input/select/select.directive';

@Component({
  selector: 'app-meteringpoint-form-address',
  imports: [
    ReactiveFormsModule,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    SearchComponent,
    TranslatePipe,
    NgClass,
    GuideCardComponent,
    SelectDirective,
    FormsModule,

  ],
  templateUrl: './meteringpoint-form-address.component.html',
  styleUrl: './meteringpoint-form-address.component.css'
})
export class MeteringpointFormAddressComponent implements OnInit {
  @ViewChild('search', { static: true }) search!: SearchComponent;

  @Input() public form!: FormGroupType<CreateMeteringPointCommand | UpdateMeteringPointCommand | ConnectionPointDto>;

  @Input() public formGroupName: string = 'installationLocationAddress';

  @Input() public fullWidth: boolean = true;

  @Input() overrideTitle?: string;

  public advancedInput: boolean = false;
  public addressFetched: boolean = false;
  public addressType: AddressType = 'address';

  public addressCarry?: AddressDto;

  /** Search Config **/
  public searchConfig: SearchConfig = {
    hotkeys: false,
    standalone: true,
    autoFocus: false,
    navigateUrl: undefined,
    callBackFunction: (value: string) => this.searchQuery(value),
  }

  public addressTypeList: KeyValue[] = [
    { key: 'address', value: 'enum.address.address' },
    { key: 'access', value: 'enum.address.access' },
  ]

  constructor(
    private readonly autocompleteService: AutocompleteService,
    private readonly toastService: ToastService,
  ) {
  }

  public ngOnInit(): void {
    this.form.get(this.formGroupName)?.disable();
  }

  /** Search Query **/
  public searchQuery(value: string): Observable<SearchResultCollection[]> {
    if (value.length > 2) {
      return this.autocompleteService.searchAddress(value, this.addressType)
        .pipe(
          map((results: any) => mapDarResults(results, this.addressType)),
          catchError(
            (error) => {
              console.error('Error searching address:', error);
              return of([]);
            }
          )
        )
    }
    return of([]);
  }

  public addressSelected(event: SearchResult): void {
    if (event.id) {
      this.autocompleteService.getById(event.id, this.addressType).subscribe((addr: any) => {
        /** Patch Address **/
        patchAddress(this.form, addr, this.formGroupName);

        /** Address Fetched **/
        this.addressFetched = true;
      })
    }
  }

  public sanitizeAddress(): void {
    this.form.markAsTouched();

    if (this.form.get(this.formGroupName)?.valid) {
      const form = this.form.get(this.formGroupName);
      if (!form) return;
      let addr = `${form.get('streetName')?.value} ${form.get('buildingNumber')?.value} ${form.get('floorIdentification')?.value} ${form.get('roomIdentification')?.value} ${form.get('postalCode')?.value} ${form.get('cityName')?.value ?? ''}`;
      addr = addr.replaceAll('null', '').replace(/ {2,}/g, ' ').trim()
      this.autocompleteService.sanitizeAddress(addr.trim()).subscribe((results) => {
        if (results.kategori === 'A' || results.kategori === 'B') {
          this.autocompleteService.getById(results.resultater[0].adresse.id).subscribe((data) => {
            if (data) {
              /** Necessary Evil **/
              const doorReference = 'dør';

              this.form.get(this.formGroupName)?.patchValue({
                streetName: data.adgangsadresse.vejstykke.navn,
                streetCode: data.adgangsadresse.vejstykke.kode,
                buildingNumber: data.adgangsadresse.husnr,
                floorIdentification: data.etage,
                roomIdentification: data[doorReference],
                subDivisionName: data.adgangsadresse.supplerendebynavn,
                postalCode: data.adgangsadresse.postnummer.nr,
                cityName: data.adgangsadresse.postnummer.navn,
                municipalityCode: data.adgangsadresse.kommune.kode,
                darReference: data.id,
                country: 'DK'
              });
              this.toastService.show('address.addresssanitized');
            }
          });
        }
      });
    }
  }

  public activateSearch(): void {
    this.advancedInput = false;

    if (this.addressCarry) {
      const addr = this.form.get(this.formGroupName)?.value;
      const { darReference: _, ...existingAddrRest } = this.addressCarry;
      const { darReference: __, ...addrRest } = addr;

      if (JSON.stringify(existingAddrRest) === JSON.stringify(addrRest)) {
        this.form.get(this.formGroupName)?.patchValue(this.addressCarry);
      }
    }

    this.form.get(this.formGroupName)?.disable();
    this.form.get(`${this.formGroupName}.streetName`)?.removeValidators(Validators.required);
    this.form.get(`${this.formGroupName}.buildingNumber`)?.removeValidators(Validators.required);
    this.form.get(`${this.formGroupName}.postalCode`)?.removeValidators(Validators.required);
    this.form.get(`${this.formGroupName}.country`)?.removeValidators(Validators.required);
  }

  public activateAdvanced(): void {
    this.advancedInput = true;

    this.addressCarry = this.form.get(this.formGroupName)?.value;

    this.form.get(this.formGroupName)?.enable();
    this.form.get(`${this.formGroupName}.streetName`)?.setValidators(Validators.required);
    this.form.get(`${this.formGroupName}.buildingNumber`)?.setValidators(Validators.required);
    this.form.get(`${this.formGroupName}.postalCode`)?.setValidators(Validators.required);
    this.form.get(`${this.formGroupName}.country`)?.setValidators(Validators.required);

    // Reset darReference
    this.form.get(`${this.formGroupName}.darReference`)?.reset();
    this.form.get(`${this.formGroupName}.darReference`)?.disable();
  }
}
